import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import SectionActions from './SectionActions';
import PrintHeader from './PrintHeader';
import { QRCodeSVG } from 'qrcode.react';
import {
  CreditCard, Clock, MapPin, LogIn, LogOut, Search,
  QrCode, History, Users, Building2, CheckCircle2, XCircle,
  ArrowLeft, Plus, Loader2, Trash2, UserPlus, Edit, Save, X
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BadgeCantiere = () => {
  const [activeTab, setActiveTab] = useState('timbra');
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [operai, setOperai] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [timbrature, setTimbrature] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [location, setLocation] = useState(null);
  const [showBadge, setShowBadge] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddWorker, setShowAddWorker] = useState(false);
  const [newWorker, setNewWorker] = useState({ name: '', role: '', cantiereId: '', phone: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editingTimbratura, setEditingTimbratura] = useState(null);
  const [editTimbData, setEditTimbData] = useState({ date: '', time: '', type: '' });
  const [storicoDate, setStoricoDate] = useState(new Date().toISOString().split('T')[0]);
  const [storicoTimbrature, setStoricoTimbrature] = useState([]);
  const [manualWorker, setManualWorker] = useState(null);
  const [manualData, setManualData] = useState({ date: '', inTime: '', outTime: '' });
  const [geoAlert, setGeoAlert] = useState(null);
  const pageRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [o, c, t] = await Promise.all([
        api.getOperai(),
        api.getCantieri(),
        api.getTimbrature(new Date().toISOString().split('T')[0]),
      ]);
      setOperai(o);
      setCantieri(c);
      setTimbrature(t);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Load storico for selected date
  const loadStorico = useCallback(async () => {
    try {
      const t = await api.getTimbrature(storicoDate);
      setStoricoTimbrature(t);
    } catch (e) { console.error(e); }
  }, [storicoDate]);

  useEffect(() => { loadStorico(); }, [loadStorico]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setLocation({ lat: 45.4642, lng: 9.1900 })
      );
    }
  }, []);

  // Geofence monitoring - check every 2 minutes
  useEffect(() => {
    const checkGeo = async () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(async (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        // Check all on-site workers
        const onSite = operai.filter(o => o.status === 'on_site');
        for (const worker of onSite) {
          try {
            const result = await api.checkGeofence(worker.id, pos.coords.latitude, pos.coords.longitude);
            if (!result.inside) {
              setGeoAlert({
                worker: result.workerName,
                cantiere: result.cantiereName,
                distance: result.distance,
                radius: result.radius,
                message: result.message
              });
              // Vibrate phone if supported
              if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 200]);
            }
          } catch {}
        }
      });
    };
    checkGeo();
    const interval = setInterval(checkGeo, 120000); // Every 2 minutes
    return () => clearInterval(interval);
  }, [operai]);

  const filteredWorkers = operai.filter(w =>
    !searchQuery ||
    w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.badge.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const onSiteWorkers = operai.filter(o => o.status === 'on_site');

  const handleTimbratura = async (worker, type) => {
    try {
      await api.createTimbratura({
        workerId: worker.id, type,
        lat: location?.lat || 0, lng: location?.lng || 0,
      });
      setLastAction({ worker: worker.name, type, time: new Date() });
      setTimeout(() => setLastAction(null), 4000);
      loadData();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore nella timbratura';
      alert(msg);
    }
  };

  const handleAddWorker = async () => {
    if (!newWorker.name || !newWorker.role || !newWorker.cantiereId) return;
    try {
      await api.createOperaio(newWorker);
      setNewWorker({ name: '', role: '', cantiereId: '', phone: '' });
      setShowAddWorker(false);
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteWorker = async (id) => {
    try { await api.deleteOperaio(id); setConfirmDelete(null); loadData(); } catch (e) { console.error(e); }
  };

  const openEditTimbratura = (t) => {
    const dt = new Date(t.timestamp);
    setEditTimbData({
      date: dt.toISOString().split('T')[0],
      time: dt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', hour12: false }),
      type: t.type
    });
    setEditingTimbratura(t);
  };

  const handleSaveTimbratura = async () => {
    if (!editingTimbratura || !editTimbData.date || !editTimbData.time) return;
    try {
      const newTimestamp = `${editTimbData.date}T${editTimbData.time}:00`;
      await api.updateTimbratura(editingTimbratura.id, {
        timestamp: newTimestamp,
        type: editTimbData.type
      });
      setEditingTimbratura(null);
      loadData(); loadStorico();
    } catch (e) { console.error(e); }
  };

  const handleDeleteTimbratura = async (id) => {
    try { await api.deleteTimbratura(id); loadData(); loadStorico(); } catch (e) { console.error(e); }
  };

  const handleManualHours = async () => {
    if (!manualWorker || !manualData.date || !manualData.inTime) return;
    try {
      await api.createTimbratura({ workerId: manualWorker.id, type: 'ingresso', lat: 0, lng: 0, timestamp: `${manualData.date}T${manualData.inTime}:00` });
      if (manualData.outTime) {
        await api.createTimbratura({ workerId: manualWorker.id, type: 'uscita', lat: 0, lng: 0, timestamp: `${manualData.date}T${manualData.outTime}:00` });
      }
      setManualWorker(null);
      setManualData({ date: '', inTime: '', outTime: '' });
      loadData(); loadStorico();
    } catch (e) { console.error(e); }
  };

  const getInitials = (name) => name.split(' ').map(n => n[0]).join('').toUpperCase();
  const getCantiereName = (cId) => cantieri.find(c => c.id === cId)?.name || '';

  if (loading) return <div className="page-container"><div className="loading-center"><Loader2 size={32} className="spin" /></div></div>;

  // Badge full view
  if (showBadge && selectedWorker) {
    const worker = selectedWorker;
    return (
      <div className="page-container">
        <div className="page-header">
          <div className="header-left">
            <button className="back-btn" onClick={() => { setShowBadge(false); setSelectedWorker(null); }}>
              <ArrowLeft size={20} />
            </button>
            <h1 className="page-title">Badge Digitale</h1>
          </div>
        </div>
        <div className="badge-display">
          <div className="badge-card-full">
            <div className="badge-card-header">
              <div className="badge-company-name">COLABELLA GROUP</div>
              <div className="badge-type">BADGE DI CANTIERE</div>
            </div>
            <div className="badge-card-body">
              <div className="badge-avatar-large"><span>{getInitials(worker.name)}</span></div>
              <h2 className="badge-worker-name">{worker.name}</h2>
              <p className="badge-worker-role">{worker.role}</p>
              <div className="badge-qr">
                <QRCodeSVG
                  value={JSON.stringify({ badge: worker.badge, name: worker.name, company: 'Colabella Group' })}
                  size={180} level="H" includeMargin bgColor="#ffffff" fgColor="#1f2937"
                />
              </div>
              <div className="badge-code">{worker.badge}</div>
              <div className="badge-cantiere"><Building2 size={14} /><span>{getCantiereName(worker.cantiereId)}</span></div>
              <div className={`badge-status-indicator ${worker.status === 'on_site' ? 'badge-on' : 'badge-off'}`}>
                {worker.status === 'on_site' ? <><CheckCircle2 size={16} /> In Cantiere</> : <><XCircle size={16} /> Fuori Cantiere</>}
              </div>
            </div>
            <div className="badge-card-actions">
              <button className="badge-action-btn badge-ingresso" onClick={() => handleTimbratura(worker, 'ingresso')}>
                <LogIn size={20} /><span>Ingresso</span>
              </button>
              <button className="badge-action-btn badge-uscita" onClick={() => handleTimbratura(worker, 'uscita')}>
                <LogOut size={20} /><span>Uscita</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" ref={pageRef}>
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Badge Cantiere</h1>
          <p className="page-subtitle">Gestione timbrature e badge digitali</p>
        </div>
        <div className="header-right">
          <SectionActions sectionTitle="Badge Cantiere" contentRef={pageRef} />
          <div className="live-clock">
            <Clock size={18} />
            <span className="clock-time">{currentTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
          </div>
        </div>
      </div>

      {/* BIG ADD WORKER BUTTON */}
      <div className="action-banner">
        <Dialog open={showAddWorker} onOpenChange={setShowAddWorker}>
          <DialogTrigger asChild>
            <button className="big-action-btn">
              <UserPlus size={20} />
              <span>Aggiungi Nuovo Operaio</span>
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuovo Operaio</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field">
                <Label>Nome Completo *</Label>
                <Input value={newWorker.name} onChange={e => setNewWorker({...newWorker, name: e.target.value})} placeholder="Es: Mario Verdi" />
              </div>
              <div className="form-field">
                <Label>Ruolo *</Label>
                <Input value={newWorker.role} onChange={e => setNewWorker({...newWorker, role: e.target.value})} placeholder="Es: Muratore, Elettricista, Carpentiere" />
              </div>
              <div className="form-field">
                <Label>Cantiere di Assegnazione *</Label>
                <Select value={newWorker.cantiereId} onValueChange={v => setNewWorker({...newWorker, cantiereId: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleziona cantiere" /></SelectTrigger>
                  <SelectContent>
                    {cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="form-field">
                <Label>Telefono</Label>
                <Input value={newWorker.phone} onChange={e => setNewWorker({...newWorker, phone: e.target.value})} placeholder="+39 333 ..." />
              </div>
              <button className="form-submit" onClick={handleAddWorker} disabled={!newWorker.name || !newWorker.role || !newWorker.cantiereId}>
                Aggiungi Operaio
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {lastAction && (
        <div className={`action-toast ${lastAction.type === 'ingresso' ? 'toast-green' : 'toast-red'}`}>
          <CheckCircle2 size={18} />
          <span>{lastAction.worker} — {lastAction.type === 'ingresso' ? 'Ingresso' : 'Uscita'} registrato alle {lastAction.time.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      )}

      {/* Geofence Alert */}
      {geoAlert && (
        <div className="geo-alert">
          <div className="geo-alert-icon">
            <MapPin size={24} />
          </div>
          <div className="geo-alert-content">
            <strong>ATTENZIONE — Fuori dal Georecinto!</strong>
            <p>{geoAlert.worker} è a {geoAlert.distance}m dal cantiere {geoAlert.cantiere} (limite: {geoAlert.radius}m)</p>
            <p className="geo-alert-time">Ore {new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })} — {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            <p className="geo-alert-msg">Torna all'interno dell'area del cantiere per poter timbrare.</p>
          </div>
          <button className="geo-alert-close" onClick={() => setGeoAlert(null)}>
            <X size={18} />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="badge-stats-row">
        <div className="badge-stat">
          <div className="badge-stat-icon" style={{ background: '#ecfdf5' }}><Users size={18} color="#22c55e" /></div>
          <div><span className="badge-stat-value">{onSiteWorkers.length}</span><span className="badge-stat-label">In Cantiere</span></div>
        </div>
        <div className="badge-stat">
          <div className="badge-stat-icon" style={{ background: '#eff6ff' }}><Clock size={18} color="#3b82f6" /></div>
          <div><span className="badge-stat-value">{timbrature.length}</span><span className="badge-stat-label">Timbrature Oggi</span></div>
        </div>
        <div className="badge-stat">
          <div className="badge-stat-icon" style={{ background: '#fef3c7' }}><Building2 size={18} color="#f59e0b" /></div>
          <div><span className="badge-stat-value">{cantieri.filter(c => c.status === 'attivo').length}</span><span className="badge-stat-label">Cantieri Attivi</span></div>
        </div>
        {location && (
          <div className="badge-stat">
            <div className="badge-stat-icon" style={{ background: '#f3e8ff' }}><MapPin size={18} color="#8b5cf6" /></div>
            <div><span className="badge-stat-value geo-value">{location.lat.toFixed(4)}</span><span className="badge-stat-label">GPS Attivo</span></div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="badge-tabs">
        <TabsList className="badge-tabslist">
          <TabsTrigger value="timbra" className="badge-tab"><CreditCard size={16} /><span>Timbra</span></TabsTrigger>
          <TabsTrigger value="operai" className="badge-tab"><Users size={16} /><span>Operai ({operai.length})</span></TabsTrigger>
          <TabsTrigger value="storico" className="badge-tab"><History size={16} /><span>Storico</span></TabsTrigger>
        </TabsList>

        {/* Tab: Timbra */}
        <TabsContent value="timbra" className="tab-content">
          <div className="search-bar">
            <Search size={18} className="search-icon" />
            <Input placeholder="Cerca operaio per nome o badge..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-input" />
          </div>
          <div className="workers-list">
            {filteredWorkers.map((worker) => (
              <div key={worker.id} className="worker-card">
                <div className="worker-left">
                  <div className={`worker-avatar ${worker.status === 'on_site' ? 'avatar-online' : ''}`}><span>{getInitials(worker.name)}</span></div>
                  <div className="worker-info">
                    <h4 className="worker-name">{worker.name}</h4>
                    <p className="worker-role">{worker.role} — {worker.badge}</p>
                    <p className="worker-cantiere"><Building2 size={12} /> {getCantiereName(worker.cantiereId)}</p>
                  </div>
                </div>
                <div className="worker-right">
                  <span className={`worker-status ${worker.status === 'on_site' ? 'status-on' : 'status-off'}`}>
                    {worker.status === 'on_site' ? 'In Cantiere' : 'Assente'}
                  </span>
                  <div className="worker-actions-large">
                    <button className="action-btn-lg btn-ingresso-lg" onClick={() => handleTimbratura(worker, 'ingresso')}>
                      <LogIn size={16} /> Ingresso
                    </button>
                    <button className="action-btn-lg btn-uscita-lg" onClick={() => handleTimbratura(worker, 'uscita')}>
                      <LogOut size={16} /> Uscita
                    </button>
                    <button className="action-btn-lg btn-manual-lg" onClick={() => { setManualWorker(worker); setManualData({ date: new Date().toISOString().split('T')[0], inTime: '08:00', outTime: '17:00' }); }}>
                      <Clock size={16} /> Inserisci Ore
                    </button>
                    <button className="action-btn-lg btn-badge-lg" onClick={() => { setSelectedWorker(worker); setShowBadge(true); }}>
                      <QrCode size={16} /> Badge
                    </button>
                    <button className="action-btn-lg btn-delete-lg" onClick={() => setConfirmDelete(worker.id)}>
                      <Trash2 size={16} /> Elimina
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {filteredWorkers.length === 0 && <p className="empty-text">Nessun operaio trovato. Aggiungi il primo!</p>}
          </div>
        </TabsContent>

        {/* Tab: Operai */}
        <TabsContent value="operai" className="tab-content">
          <div className="operai-grid">
            {operai.map((worker) => (
              <div key={worker.id} className="operaio-card">
                <div className={`operaio-avatar ${worker.status === 'on_site' ? 'avatar-online' : ''}`}><span>{getInitials(worker.name)}</span></div>
                <h4>{worker.name}</h4>
                <p className="operaio-role">{worker.role}</p>
                <p className="operaio-badge">{worker.badge}</p>
                <span className={`operaio-status ${worker.status === 'on_site' ? 'status-on' : 'status-off'}`}>
                  {worker.status === 'on_site' ? 'In Cantiere' : 'Assente'}
                </span>
                <p className="operaio-cantiere"><Building2 size={12} /> {getCantiereName(worker.cantiereId)}</p>
                <div className="operaio-card-actions">
                  <button className="operaio-btn" onClick={() => { setSelectedWorker(worker); setShowBadge(true); }}>
                    <QrCode size={14} /> Badge QR
                  </button>
                  <button className="operaio-btn operaio-btn-danger" onClick={() => setConfirmDelete(worker.id)}>
                    <Trash2 size={14} /> Elimina
                  </button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Tab: Storico */}
        <TabsContent value="storico" className="tab-content">
          <div className="storico-date-nav">
            <button className="storico-day-btn" onClick={() => {
              const d = new Date(storicoDate); d.setDate(d.getDate() - 1); setStoricoDate(d.toISOString().split('T')[0]);
            }}>&#8592; Giorno Prima</button>
            <input type="date" value={storicoDate} onChange={e => setStoricoDate(e.target.value)} className="date-input" />
            <button className="storico-day-btn" onClick={() => {
              const d = new Date(storicoDate); d.setDate(d.getDate() + 1); setStoricoDate(d.toISOString().split('T')[0]);
            }}>Giorno Dopo &#8594;</button>
            <button className="storico-today-btn" onClick={() => setStoricoDate(new Date().toISOString().split('T')[0])}>Oggi</button>
          </div>
          <div className="storico-header">
            <h3>Timbrature del {new Date(storicoDate + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h3>
            <span className="storico-count">{storicoTimbrature.length} registrazioni</span>
          </div>
          <div className="storico-list">
            {storicoTimbrature.map((t) => (
              <div key={t.id} className="storico-item">
                <div className={`storico-type ${t.type === 'ingresso' ? 'type-in' : 'type-out'}`}>
                  {t.type === 'ingresso' ? <LogIn size={16} /> : <LogOut size={16} />}
                </div>
                <div className="storico-info">
                  <span className="storico-name">{t.workerName}</span>
                  <span className="storico-detail">{t.cantiereName}</span>
                </div>
                <div className="storico-time-col">
                  <span className="storico-time">{new Date(t.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                  <span className="storico-date-label">{new Date(t.timestamp).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric' })}</span>
                </div>
                <div className="storico-actions">
                  <button className="storico-edit-btn" onClick={() => openEditTimbratura(t)} title="Modifica orario">
                    <Edit size={14} /> Modifica
                  </button>
                  <button className="storico-del-btn" onClick={() => handleDeleteTimbratura(t.id)} title="Elimina">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
            {storicoTimbrature.length === 0 && <div className="empty-storico"><History size={40} /><p>Nessuna timbratura per questa data</p></div>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Timbratura Dialog */}
      {editingTimbratura && (
        <Dialog open={!!editingTimbratura} onOpenChange={() => setEditingTimbratura(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifica Timbratura — {editingTimbratura.workerName}</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field">
                <Label>Tipo</Label>
                <Select value={editTimbData.type} onValueChange={v => setEditTimbData({...editTimbData, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ingresso">Ingresso</SelectItem>
                    <SelectItem value="uscita">Uscita</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-field">
                <Label>Data</Label>
                <Input type="date" value={editTimbData.date} onChange={e => setEditTimbData({...editTimbData, date: e.target.value})} />
              </div>
              <div className="form-field">
                <Label>Ora</Label>
                <Input type="time" value={editTimbData.time} onChange={e => setEditTimbData({...editTimbData, time: e.target.value})} />
              </div>
              <button className="form-submit" onClick={handleSaveTimbratura}>
                <Save size={16} style={{ marginRight: 6 }} /> Salva Modifiche
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Manual Hours Dialog */}
      {manualWorker && (
        <Dialog open={!!manualWorker} onOpenChange={() => setManualWorker(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Inserisci Ore — {manualWorker.name}</DialogTitle></DialogHeader>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px' }}>
              Inserisci manualmente data e orari per questo operaio.
            </p>
            <div className="form-fields">
              <div className="form-field">
                <Label>Data *</Label>
                <Input type="date" value={manualData.date} onChange={e => setManualData({...manualData, date: e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-field">
                  <Label>Ora Ingresso *</Label>
                  <Input type="time" value={manualData.inTime} onChange={e => setManualData({...manualData, inTime: e.target.value})} />
                </div>
                <div className="form-field">
                  <Label>Ora Uscita</Label>
                  <Input type="time" value={manualData.outTime} onChange={e => setManualData({...manualData, outTime: e.target.value})} />
                </div>
              </div>
              <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0' }}>
                Se lasci vuota l'ora di uscita, verrà registrato solo l'ingresso.
              </p>
              <button className="form-submit" onClick={handleManualHours} disabled={!manualData.date || !manualData.inTime}>
                Registra Ore
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirm Delete Dialog */}
      {confirmDelete && (
        <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Conferma Eliminazione</DialogTitle></DialogHeader>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '8px 0 16px' }}>
              Sei sicuro di voler eliminare questo operaio? L'azione non può essere annullata.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="form-cancel" onClick={() => setConfirmDelete(null)}>Annulla</button>
              <button className="form-submit-danger" onClick={() => handleDeleteWorker(confirmDelete)}>Elimina Operaio</button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default BadgeCantiere;
